'use client';

import NextImage from 'next/image';

import { LibraryIcon, LibraryIconSymbol } from '@repo/icons/library-icon';
import { PlusCircle } from 'lucide-react';

import { Link } from '~/components/common/link';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { Icon } from '~/components/ui/icon';
import { ctaLinkResolver, truncate } from '~/lib/utils';
import { BulletinItemFragment } from '~/server/generated/contentful-graphql';

export function BulletinBoardStackedModal({ bulletins }: { bulletins: BulletinItemFragment[] }) {
  const getGridClass = () => {
    if (!bulletins || bulletins.length === 0) return '';

    switch (bulletins.length) {
      case 1:
        return 'sm:grid-cols-1 max-w-md mx-auto';
      case 2:
        return 'grid-cols-1 sm:grid-cols-2';
      default:
        return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }
  };

  return (
    <ol className={`grid gap-8 ${getGridClass()}`}>
      {bulletins?.map((bulletin, index) => {
        const tmpHackHideImage = /Bulletin Color Bar/gm.test(bulletin.image?.title ?? '');
        const ctaHref = ctaLinkResolver({
          ctaLink: bulletin.ctaLink,
          ctaLinkContentSlug: bulletin.ctaLinkContentCollection?.items[0]?.slug,
          ctaLinkContentType: bulletin.ctaLinkContentCollection?.items[0]?.__typename,
          ctaLinkAssetUrl: bulletin.ctaLinkAsset?.url,
        });
        return (
          <li key={bulletin.sys.id}>
            <Dialog>
              <DialogTrigger asChild>
                <button className='block h-full w-full cursor-pointer text-left'>
                  <div className='bg-blue-light-50 flex h-full max-w-md flex-col rounded-xl px-6 pb-6 lg:max-w-sm lg:px-8 lg:pb-6'>
                    <figure className='leading-0 relative -mx-6 mb-6 self-start lg:-mx-8 lg:mb-8'>
                      {bulletin.image?.url && !tmpHackHideImage && (
                        <NextImage
                          alt={String(bulletin.image.title)}
                          className='max-w-full rounded-t-xl'
                          height={543}
                          src={`${bulletin.image.url}?fit=fill&w=800&h=534`}
                          width={800}
                        />
                      )}
                      {bulletin.icon && (
                        <div className='text-yellow absolute -bottom-px left-0 flex h-12 w-12 items-end rounded-tr-full bg-white'>
                          <LibraryIcon height={28} use={bulletin.icon as LibraryIconSymbol} width={28} />
                        </div>
                      )}
                      {bulletin.imageCaption && (
                        <figcaption className='font-greycliff-bold text-shadow-md absolute inset-x-0 bottom-0 p-4 text-sm leading-tight text-white'>
                          {bulletin.imageCaption}
                        </figcaption>
                      )}
                    </figure>
                    <div className='flex h-full flex-col justify-between'>
                      <div>
                        {bulletin.title && (
                          <h2 className='font-greycliff-extrabold text-foreground  text-lg lg:text-xl'>
                            {bulletin.title}
                          </h2>
                        )}
                        {bulletin.copy && <p className='body-base mt-2'>{truncate(bulletin.copy, 170)}</p>}
                      </div>

                      <div className='font-greycliff-extrabold text-blue mt-4 flex items-center justify-end gap-2'>
                        More <PlusCircle width={18} height={18} />
                      </div>
                    </div>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className='hide-scrollbar lg:[&>button>svg]:text-foreground max-h-[720px] max-w-2xl overflow-y-auto rounded-2xl pt-0 lg:p-10 lg:pt-10'>
                <div className='space-y-6'>
                  {bulletin.image?.url && !tmpHackHideImage && (
                    <div className='relative -mx-6 mb-6 overflow-hidden rounded-t-xl lg:mx-0 lg:rounded-xl'>
                      <NextImage
                        alt={String(bulletin.image.title)}
                        className='w-full object-cover'
                        height={373}
                        src={`${bulletin.image.url}?fit=fill&w=560&h=373`}
                        width={560}
                      />
                      {bulletin.icon && (
                        <div className='text-yellow absolute bottom-4 left-4 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg'>
                          <LibraryIcon height={32} use={bulletin.icon as LibraryIconSymbol} width={32} />
                        </div>
                      )}
                      {bulletin.imageCaption && (
                        <figcaption className='font-greycliff-bold text-shadow-md absolute inset-x-0 bottom-0 p-4 text-sm leading-tight text-white'>
                          {bulletin.imageCaption}
                        </figcaption>
                      )}
                    </div>
                  )}
                  <div className='px-6 pb-6 lg:p-0'>
                    <DialogTitle className={bulletin.title ? 'heading-sm text-foreground mb-4' : 'sr-only'}>
                      {bulletin.title ? bulletin.title : `Bulletin ${index + 1}`}
                    </DialogTitle>
                    {bulletin.copy &&
                      bulletin.copy.split('\n').map((paragraph, i) => (
                        <p key={i} className={`text-lg leading-relaxed ${i > 0 ? 'mt-4' : ''}`}>
                          {paragraph}
                        </p>
                      ))}
                    {bulletin.ctaTitle && ctaHref && (
                      <Link
                        className='font-greycliff-extrabold text-blue mt-4 flex items-center gap-1 text-xs capitalize'
                        href={ctaHref}
                      >
                        {bulletin.ctaTitle}
                        <Icon height={16} use='chevron-right' width={16} />
                      </Link>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </li>
        );
      })}
    </ol>
  );
}
